import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tab,
  Paper,
  Typography,
  Menu,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  Link,
  Divider,
  ListItem,
  ListItemText,
} from '@mui/material';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import { getAllGroups, getUserStatsForGroup } from '../../graphql/queries';
import { API } from '../../services/amplifyCompat';
import { ExpandMore } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled } from '@mui/material/styles';
import { SortLeaderboard } from '../SortLeaderboard';
import { useNavigate } from 'react-router-dom';
import useTranslation from '../customHooks/translations';
import { CurrentPlaceNumbers } from '../CurrentPlaceNumbers';
import { UserStatsCharts } from '../UserStatsCharts';

const StyledTableBody = styled(TableBody)`
  td {
    color: #0F172A;
    font-weight: 500;
  }
  .currentGroupOrUser {
    background: linear-gradient(91.49deg, #F0FDFA 0.29%, #CCFBF1 100%);
    th,td {
      color: #0F766E;
      font-weight: 700;
    }
    a {
      color: #0F766E;
    }
  }
`;

const GROUP_LEADERBOARD_TABS = {
  members: 'members',
  globalGroups: 'globalGroups',
};

const GroupPageLeaderboard = ({ currentGroup, groupMembers, userId, user }) => {
  const navigate = useNavigate();
  const translation = useTranslation();
  const tabLabels = {
    [GROUP_LEADERBOARD_TABS.members]: translation.groupMembers,
    [GROUP_LEADERBOARD_TABS.globalGroups]: translation.globalGroups,
  };
  const filters = [
    { name: translation.totalCO2Saved, property: 'total_co2' },
    { name: translation.weeklyCO2Saved, property: 'weekly_co2' },
    { name: translation.totalPoints, property: 'total_points' },
    { name: translation.weeklyPoints, property: 'weekly_points' },
  ];
  const [groups, setGroups] = useState();
  const [selectedTab, setSelectedTab] = useState(GROUP_LEADERBOARD_TABS.members);
  const [selectedFilter, setSelectedFilter] = useState(filters[0]);
  const [filteredGroups, setFilteredGroups] = useState();
  const [filteredMembers, setFilteredMembers] = useState();
  const [openFilterMenu, setOpenFilterMenu] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [userStats, setUserStats] = useState();
  const [userContributionPercentages, setUserContributionPercentages] =
    useState([]);

  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const hideCharts = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (userStats) {
      const percentageData = [
        {
          title: translation.totalCO2,
          value: Math.round(
            (userStats.total_co2 / currentGroup.total_co2) * 100
          ),
        },
        {
          title: translation.weeklyCO2,
          value: Math.round(
            (userStats.weekly_co2 / currentGroup.weekly_co2) * 100
          ),
        },
        {
          title: translation.totalPoints,
          value: Math.round(
            (userStats.total_points / currentGroup.total_points) * 100
          ),
        },
        {
          title: translation.weeklyPoints,
          value: Math.round(
            (userStats.weekly_points / currentGroup.weekly_points) * 100
          ),
        },
      ];
      setUserContributionPercentages(percentageData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStats]);

  // avoid a layout jump in the table when reaching the last page with empty rows
  let emptyRows =
    page <= 0
      ? 0
      : selectedTab === GROUP_LEADERBOARD_TABS.globalGroups
        ? Math.max(0, (1 + page) * rowsPerPage - groups.length)
        : selectedTab === GROUP_LEADERBOARD_TABS.members
          ? Math.max(0, (1 + page) * rowsPerPage - groupMembers.length)
          : 0;

  useEffect(() => {
    const getUserStats = async () => {
      const res = await API.graphql({
        query: getUserStatsForGroup,
        variables: { user_id: user.user_id, group_id: currentGroup.group_id },
      });
      setUserStats(res.data.getUserStatsForGroup);
    };

    const getGroups = async () => {
      const res = await API.graphql({
        query: getAllGroups,
      });
      setGroups(res.data.getAllGroups);
    };

    currentGroup && user && getUserStats();
    getGroups();
  }, [currentGroup, user]);

  //filters groups when group state is initially set, filters groups and group members every time a new filter is selected
  // Members tab only needs groupMembers; don't wait for the all-groups fetch
  useEffect(() => {
    if (groupMembers || groups) {
      handleFilterSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, groupMembers, selectedFilter, selectedTab]);

  //sets filter back to default (total co2) and sets page back to first page on tab change
  const handleTabChange = (e, newValue) => {
    setSelectedTab(newValue);
    setSelectedFilter(filters[0]);
    setPage(0);
  };

  /** functions for filter menu */
  const handleClick = (e) => {
    setFilterMenuAnchor(e.currentTarget);
    setOpenFilterMenu(true);
  };

  const handleClose = () => {
    setFilterMenuAnchor(null);
    setOpenFilterMenu(false);
  };

  const handleFilterSelection = () => {
    const propertySelected = selectedFilter.property;
    //if Global Group tab is selected, apply the selected filter to all groups
    if (selectedTab === GROUP_LEADERBOARD_TABS.globalGroups && groups) {
      //make a mutable copy of the groups array, sort that array and update the in state version of groups
      let groupsArrayCopy = [...groups];
      let sortedByFilter = groupsArrayCopy.sort(
        (a, b) => b[propertySelected] - a[propertySelected]
      );
      setFilteredGroups(sortedByFilter);
    }
    //if Group Members tab is selected, apply the selected filter to all users
    if (selectedTab === GROUP_LEADERBOARD_TABS.members && groupMembers) {
      //make a mutable copy of the groupMembers array, sort that array and update the in state version of groupMembers
      let groupMemberArrayCopy = [...groupMembers];
      let sortedByFilter = groupMemberArrayCopy.sort(
        (a, b) => b[propertySelected] - a[propertySelected]
      );
      setFilteredMembers(sortedByFilter);
    }
    handleClose();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderUserContributionListItems = () => {
    return userContributionPercentages.map((stat, index) => (
      <React.Fragment key={index}>
        <Divider flexItem />
        <ListItem>
          <ListItemText primary={stat.title} />
          <span>
            <Typography variant="body1">
              {stat.value ? stat.value : 0}%
            </Typography>
          </span>
        </ListItem>
      </React.Fragment>
    ));
  };

  /** function for displaying the leaderboard tables */

  const renderTable = () => {
    return (
      <>
        {/* if Global Groups tab is selected, render group current place */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            mt: { xs: '2em', sm: '0' },
          }}
        >
          {filteredGroups && selectedTab === GROUP_LEADERBOARD_TABS.globalGroups && (
            <CurrentPlaceNumbers
              placeNumber={
                filteredGroups.findIndex(
                  (group) => group.group_name === currentGroup.group_name
                ) + 1
              }
              totalNumber={filteredGroups.length}
            />
          )}
          {/* if Group Members tab is selected, check if user is a group member, then render user's current place. If user doesn't belong to the group, don't render current place */}
          {filteredMembers &&
            selectedTab === GROUP_LEADERBOARD_TABS.members &&
            groupMembers.findIndex((member) => member.user_id === userId) !==
            -1 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexDirection: hideCharts ? 'column' : 'row',
                  flexWrap: 'wrap',
                }}
              >
                <CurrentPlaceNumbers
                  placeNumber={
                    filteredMembers.findIndex(
                      (member) => member.user_id === userId
                    ) + 1
                  }
                  totalNumber={groupMembers.length}
                />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-evenly',
                    flexShrink: 1,
                  }}
                >
                  {hideCharts ? (
                    <Accordion sx={{ my: '1.5em' }}>
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        aria-controls="user-contributions-content"
                      >
                        <Typography>{translation.myContributions}</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <List dense>{renderUserContributionListItems()}</List>
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    <UserStatsCharts group={currentGroup} />
                  )}
                </Box>
              </Box>
            )}
        </Box>
        <TableContainer component={Paper} sx={{ mt: '1em' }}>
          <Table stickyHeader aria-label="group leaderboard">
            <caption>
              {translation.formatString(translation.leaderboardDisplaying, {
                tab: tabLabels[selectedTab] || selectedTab,
              })}
              {selectedFilter.name}
            </caption>
            <TableHead>
              <TableRow>
                <TableCell>{translation.rank}</TableCell>
                <TableCell align="left">{translation.name}</TableCell>
                <TableCell align="right">{translation.totalCO2}</TableCell>
                <TableCell align="right">{translation.totalPoints}</TableCell>
                <TableCell align="right">{translation.weeklyCO2}</TableCell>
                <TableCell align="right">{translation.weeklyPoints}</TableCell>
              </TableRow>
            </TableHead>
            <StyledTableBody>
              {/* if Global Groups tab is selected, display all groups data in table body*/}
              {selectedTab === GROUP_LEADERBOARD_TABS.globalGroups &&
                filteredGroups &&
                (rowsPerPage > 0
                  ? filteredGroups.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                  : filteredGroups
                ).map((group, index) => (
                  <TableRow
                    key={group.group_id}
                    className={
                      currentGroup.group_name === group.group_name
                        ? 'currentGroupOrUser'
                        : ''
                    }
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ color: theme.palette.primary.main }}
                    >
                      {/* if on page 1, add 1 to the index to get item rankings starting from 1. On further pages, add the number of items on all the pages prior to the item's index + 1 value to get the correct ranking */}
                      {page > 0 ? rowsPerPage * page + index + 1 : index + 1}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      <Link
                        onClick={() => {
                          navigate(`/group-profile/${encodeURI(group.group_name)}`);
                        }}
                        sx={{
                          color: '#0F172A',
                          fontSize: '1.1em',
                          textDecorationColor: '#0D9488',
                          ':hover': { opacity: '0.6', cursor: 'pointer' },
                        }}
                      >
                        {group.group_name}
                      </Link>
                    </TableCell>
                    <TableCell align="right">
                      {group.total_co2 > 9999
                        ? `${(group.total_co2 / 1000).toFixed(2)} kg`
                        : `${Math.ceil(group.total_co2)} g`}
                    </TableCell>
                    <TableCell align="right">{group.total_points.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      {group.weekly_co2 > 9999
                        ? `${(group.weekly_co2 / 1000).toFixed(2)} kg`
                        : `${Math.ceil(group.weekly_co2)} g`}
                    </TableCell>
                    <TableCell align="right">{group.weekly_points.toLocaleString()}</TableCell>
                  </TableRow>
                ))}

              {/* if Group Members tab is selected, display all member data in table body*/}
              {selectedTab === GROUP_LEADERBOARD_TABS.members &&
                filteredMembers &&
                (rowsPerPage > 0
                  ? filteredMembers.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                  : filteredMembers
                ).map((member, index) => (
                  <TableRow
                    key={member.user_id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    className={
                      userId === member.user_id ? 'currentGroupOrUser' : ''
                    }
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ color: theme.palette.primary.main }}
                    >
                      {page > 0 ? rowsPerPage * page + index + 1 : index + 1}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {member.name}
                    </TableCell>
                    <TableCell align="right">
                      {member.total_co2 > 9999
                        ? `${(member.total_co2 / 1000).toFixed(2)} kg`
                        : `${Math.ceil(member.total_co2)} g`}
                    </TableCell>
                    <TableCell align="right">{member.total_points.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      {(member.weekly_co2 ?? 0) > 9999
                        ? `${((member.weekly_co2 ?? 0) / 1000).toFixed(2)} kg`
                        : `${Math.ceil(member.weekly_co2 ?? 0)} g`}
                    </TableCell>
                    <TableCell align="right">{member.weekly_points.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </StyledTableBody>
          </Table>
        </TableContainer>
        {/* render the correct pagination options for each table */}
        {selectedTab === GROUP_LEADERBOARD_TABS.globalGroups && groups && (
          <TablePagination
            rowsPerPageOptions={[5, 10]}
            component="div"
            count={groups && groups.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
            labelRowsPerPage={translation.labelRowsPerPage}
            labelDisplayedRows={
              ({ from, to, count }) => {
                return '' + from + ' - ' + to + ' ' + translation.of + ' ' + count
              }
            }
          />
        )}
        {selectedTab === GROUP_LEADERBOARD_TABS.members && groupMembers && (
          <TablePagination
            rowsPerPageOptions={[5, 10]}
            component="div"
            count={groupMembers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
            labelRowsPerPage={translation.labelRowsPerPage}
            labelDisplayedRows={
              ({ from, to, count }) => {
                return '' + from + ' - ' + to + ' ' + translation.of + ' ' + count
              }
            }
          />
        )}
      </>
    );
  };

  // Members tab is ready once groupMembers exist; global groups tab waits for groups list
  const membersReady = Boolean(groupMembers);
  const showLeaderboard = membersReady || Boolean(groups);

  return (
    <>
      {showLeaderboard && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: 'center', sm: 'space-between' },
              alignItems: 'center',
              gap: { xs: '1em', sm: '0' },
              mb: '1em',
              width: '100%',
            }}
          >
            <Typography variant="h2">{translation.leaderboards}</Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <SortLeaderboard
                onSelectSort={handleClick}
                selectedSort={selectedFilter}
              />
              <Menu
                id="filter-menu"
                open={openFilterMenu}
                anchorEl={filterMenuAnchor}
                onClose={handleClose}
              >
                {filters.map((filter, index) => (
                  <MenuItem
                    key={index}
                    onClick={() => setSelectedFilter(filter)}
                  >
                    {filter.name}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
          <TabContext value={selectedTab}>
            <Box
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                width: '100%',
                display: { xs: 'contents', sm: 'flex' },
                padding: '0.5em',
              }}
            >
              <TabList
                orientation={mobileView ? 'horizontal' : 'vertical'}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                onChange={handleTabChange}
                aria-label="Leaderboard tabs"
                sx={{
                  borderTop: { xs: 1, sm: 1 },
                  borderRight: { xs: 0, sm: 1 },
                  borderBottom: { xs: 1, sm: 1 },
                  borderTopColor: { xs: 'divider', sm: 'transparent' },
                  borderRightColor: { xs: 'transparent', sm: 'divider' },
                  borderBottomColor: { xs: 'divider', sm: 'transparent' },
                  minWidth: 'min-content',
                }}
              >
                <Tab
                  label={translation.groupMembers}
                  value={GROUP_LEADERBOARD_TABS.members}
                />
                <Tab
                  label={translation.globalGroups}
                  value={GROUP_LEADERBOARD_TABS.globalGroups}
                />
              </TabList>
              <TabPanel
                value={GROUP_LEADERBOARD_TABS.members}
                sx={{ width: '100%', padding: { xs: '0', sm: '1.5em' } }}
              >
                {membersReady && renderTable()}
              </TabPanel>
              <TabPanel
                value={GROUP_LEADERBOARD_TABS.globalGroups}
                sx={{ width: '100%', padding: { xs: '0', sm: '1.5em' } }}
              >
                {groups && renderTable()}
              </TabPanel>
            </Box>
          </TabContext>
        </>
      )}
    </>
  );
};

export default GroupPageLeaderboard;
